import React, { useState } from "react";
import axios from "axios";
import { Send, Loader2, Trash2, StickyNote, Activity, Pencil, Receipt, FileCheck2, RefreshCw } from "lucide-react";
import { API, inputCls, btnPrimary, Empty, fmtDateTime } from "./shared";

const KIND_META = {
  note: { label: "Nota", icon: StickyNote, cls: "bg-[#0fe814] text-black" },
  status: { label: "Estado", icon: RefreshCw, cls: "bg-blue-500 text-white" },
  edit: { label: "Edición", icon: Pencil, cls: "bg-black text-[#0fe814]" },
  transaction: { label: "Transacción", icon: Receipt, cls: "bg-amber-400 text-black" },
  kyc: { label: "KYC", icon: FileCheck2, cls: "bg-purple-500 text-white" },
};

const ClientNotes = ({ appId, headers, canManage, notes, onChange }) => {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");

  const submit = async () => {
    if (text.trim().length === 0) return;
    setSaving(true);
    try {
      await axios.post(`${API}/crm/clients/${appId}/notes`, { text: text.trim() }, { headers });
      setText("");
      onChange();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta nota?")) return;
    await axios.delete(`${API}/crm/notes/${id}`, { headers });
    onChange();
  };

  const list = filter === "all" ? notes : notes.filter((n) => n.kind === filter);

  return (
    <div className="space-y-4" data-testid="crm-notes">
      {canManage && (
        <div className="bg-white border border-black/[0.06] rounded-2xl p-4">
          <textarea
            data-testid="note-input"
            className={`${inputCls} min-h-[84px] resize-y`}
            placeholder="Escribe una nota sobre este cliente (llamadas, acuerdos, seguimiento, incidencias...)"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <div className="mt-3 flex justify-end">
            <button data-testid="note-save" className={btnPrimary} onClick={submit} disabled={saving || !text.trim()}>
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Agregar nota
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {[["all", "Todo"], ...Object.entries(KIND_META).map(([k, m]) => [k, m.label])].map(([k, l]) => (
          <button key={k} data-testid={`note-filter-${k}`} onClick={() => setFilter(k)}
            className={`text-[11px] font-bold px-3 py-1.5 rounded-full border-2 transition-colors ${filter === k ? "bg-black text-white border-black" : "border-black/10 text-black/55 hover:border-black/40"}`}>
            {l}
          </button>
        ))}
      </div>

      {list.length === 0 ? <Empty icon={Activity} text="Sin actividad registrada" /> : (
        <ol className="relative border-l-2 border-black/[0.08] ml-4 space-y-4">
          {list.map((n) => {
            const meta = KIND_META[n.kind] || KIND_META.note;
            const Icon = meta.icon;
            return (
              <li key={n.id} data-testid={`note-item-${n.id}`} className="relative pl-7">
                <span className={`absolute -left-[13px] top-0.5 w-6 h-6 rounded-full flex items-center justify-center ${meta.cls}`}><Icon size={12} /></span>
                <div className="bg-white border border-black/[0.06] rounded-2xl px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[13px] font-semibold text-black whitespace-pre-wrap break-words">{n.text}</p>
                      <p className="mt-1 text-[11px] font-medium text-black/45">{meta.label} · {n.author?.name || "Sistema"} · {fmtDateTime(n.created_at)}</p>
                    </div>
                    {canManage && n.kind === "note" && (
                      <button data-testid={`note-delete-${n.id}`} title="Eliminar" className="shrink-0 p-1.5 rounded-lg hover:bg-[#ff603e]/10 text-[#ff603e]" onClick={() => remove(n.id)}><Trash2 size={13} /></button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

export default ClientNotes;
